import { supabase } from './supabaseClient';

export type ProductStatus = {
  id: string;
  name: string;
  description?: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
};

export type StockStageTransition = {
  id: string;
  stock_id: string;
  product_id: string;
  from_status_id: string;
  to_status_id: string;
  organization_id: string;
  notes?: string;
  created_at: string;
};

export class ProductStatusClient {
  static async getStatusesByOrganizationId(organizationId: string) {
    const { data, error } = await supabase
      .from('inventory_statuses')
      .select('*')
      .eq('organization_id', organizationId)
      .order('name');

    if (error) throw error;
    return data;
  }

  static async createStage(
    stage: Omit<ProductStatus, 'id' | 'created_at' | 'updated_at'>
  ) {
    const { data, error } = await supabase
      .from('inventory_statuses')
      .insert(stage)
      .select();

    if (error) throw error;
    return data[0];
  }

  static async updateStage(
    stage: Omit<ProductStatus, 'created_at' | 'updated_at'>
  ) {
    const { data, error } = await supabase
      .from('inventory_statuses')
      .update(stage)
      .eq('id', stage.id)
      .select();

    if (error) throw error;
    return data[0];
  }

  static async deleteStage(stageId: string) {
    const { error } = await supabase
      .from('inventory_statuses')
      .delete()
      .eq('id', stageId);

    if (error) throw error;
  }

  static async getStockStageHistory(stockId: string) {
    const { data, error } = await supabase
      .from('stock_stage_transitions')
      .select(
        `
        *,
        from_stage:from_stage_id(name),
        to_stage:to_stage_id(name)
      `
      )
      .eq('stock_id', stockId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  }

  static async getProductStatusHistory(productId: string) {
    const { data, error } = await supabase
      .from('stock_stage_transitions')
      .select(
        `
        *,
        from_stage:from_stage_id(name),
        to_stage:to_stage_id(name)
      `
      )
      .eq('product_id', productId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  }

  static async transitionStockStage(
    stockId: string,
    toStageId: string,
    notes?: string
  ) {
    // Get current stage and product_id
    const { data: stock } = await supabase
      .from('stocks')
      .select('current_stage_id, product_id')
      .eq('id', stockId)
      .single();

    if (!stock) throw new Error('Stock not found');

    // Create transition record
    const { data: transition, error: transitionError } = await supabase
      .from('stock_stage_transitions')
      .insert({
        stock_id: stockId,
        product_id: stock.product_id,
        from_stage_id: stock.current_stage_id,
        to_stage_id: toStageId,
        notes,
      })
      .select()
      .single();

    if (transitionError) throw transitionError;

    // Update stock's current stage
    const { error: updateError } = await supabase
      .from('stocks')
      .update({ current_stage_id: toStageId })
      .eq('id', stockId);

    if (updateError) throw updateError;

    return transition;
  }

  static async getStageMetrics(
    organizationId: string,
    timeRange: 'day' | 'week' | 'month' = 'month'
  ) {
    const { data, error } = await supabase.rpc('get_stage_metrics', {
      p_organization_id: organizationId,
      p_time_range: timeRange,
    });

    if (error) throw error;
    return data;
  }
}
